<script setup>
  const { notification } = defineProps({
    notification: { type: Object, required: true }
  })

  const client = useStrapiClient()

  const { comment, application, job, recruitmentRequest } = notification

  const title = {
    comment: 'Nouveau commentaire',
    commentMention: 'Nouvelle mention',
    jobCreated: "Votre offre vient d'être publiée",
    endDateOver: 'Votre offre est clôturée',
    endDateOverUrgent: 'Une de vos offres est clôturée avec des candidatures non traitées',
    endDateClose: 'Votre offre se clôture bientôt',
    application: 'Nouvelle candidature',
    spontaneousApplication: 'Nouvelle candidature spontanée',
    applicationStatusChange: 'Une candidature a changé de statut',
    recruiterAdded: 'Vous avez été ajouté à une offre',
    opinionRequested: 'Votre avis est demandé sur ce candidat',
    opinionSubmitted: 'Un avis a été déposé',
    opinionSummary: 'De nouveaux avis sur les candidatures',
    recruitment: 'Un nouveau collaborateur vous a rejoint',
    recruitmentRequestToReview: 'Demande de recrutement à valider',
    recruitmentRequestApproved: 'Demande de recrutement validée',
    recruitmentRequestRejected: 'Demande de recrutement refusée',
    recruitmentRequestToPublish: 'Demande de recrutement à publier',
    jobScheduledPublished: 'Votre offre programmée est en ligne',
    jobScheduledFailed: 'Votre offre programmée n’a pas pu être publiée'
  }

  const commentTarget = computed(() => {
    if (!comment) return
    if (comment.application) return getApplicationFullName(comment.application)
    else if (comment.job) return `l'offre ${comment.job.title}`
    else return "l'offre"
  })

  const commentUrl = computed(() => {
    if (!comment) return
    if (comment.application) return `/ats/candidatures/${comment.application.id}`
    else if (comment.job) return `/ats/offres/${comment.job.id}#commentaires`
    else return '/ats'
  })

  const notificationUrl = computed(() => {
    if (notification.type === 'opinionSummary')
      return job?.id
        ? `/ats/offres/${job.id}`
        : `/ats/candidatures-spontanees?type=${encodeURIComponent(notification.spontaneousType || 'employment')}`
    if (comment?.id) return commentUrl.value
    else if (application?.id)
      return `/ats/candidatures/${application.id}${['opinionRequested', 'opinionSubmitted'].includes(notification.type) ? '#avis' : ''}`
    else if (job?.id) return `/ats/offres/${job.id}`
    else if (recruitmentRequest?.id) return `/ats/demandes-recrutement/${recruitmentRequest.id}`
    else return '/ats'
  })

  const jobTitle = computed(() => application?.job?.title ?? job?.title ?? '')

  const read = async () => {
    if (!notification.id || notification.read) return
    notification.read = true
    client(`/notifications/read/${notification.id}`, { method: 'PUT' })
  }
</script>

<template>
  <NuxtLink class="flex flex-col space-y-1 bg-white px-4 py-2 hover:bg-zinc-100" :to="notificationUrl" @mouseup="read">
    <p class="text-xs text-zinc-300">{{ $dayjs(notification.createdAt).format('DD.MM.YY') }}</p>
    <div class="flex items-center space-x-2 text-wrap">
      <span class="h-2 w-2 min-w-2 rounded-full border border-viking-400 bg-white" v-if="notification.read" />
      <span class="h-2 w-2 rounded-full bg-viking-400" v-else />
      <p>{{ title[notification.type] }}</p>
    </div>
    <div class="flex flex-col text-wrap font-main text-sm font-normal">
      <div v-if="notification.type === 'opinionSummary'">
        <p>
          {{ notification.opinionCount === 1 ? 'Une candidature a reçu un avis' : `${notification.opinionCount} candidatures ont reçu un avis` }}
          {{ job ? `sur l’offre ${job.title}` : 'dans les candidatures spontanées' }}.
        </p>
      </div>
      <div v-else-if="notification.type === 'commentMention'">
        <p>{{ getFullName(comment.author) }} vous a identifié dans un commentaire sur {{ commentTarget }}:</p>
        <span class="mt-0.5 inline-block rounded-md border p-0.5">{{ comment.text.substring(0, 50) }}</span>
      </div>
      <div v-else-if="notification.type === 'comment'">
        <p>{{ getFullName(comment.author) }} vient d’ajouter un commentaire sur {{ commentTarget }}<template v-if="comment.text">:</template></p>
        <span class="mt-0.5 inline-block rounded-md border p-0.5" v-if="comment.text">{{ comment.text.substring(0, 50) }}</span>
      </div>
      <div v-else-if="application">
        <div class="flex flex-row items-center space-x-2">
          <Avatar
            class="h-8 w-8 shrink-0"
            :user="application.candidate?.user ?? application.candidate"
            :class="{
              'opacity-50': application.refused
            }"
          />
          <p v-if="notification.type === 'application'">
            {{ getApplicationFullName(application) }} vient de postuler à l'offre <strong>{{ jobTitle }}</strong
            >.
          </p>
          <p v-else-if="notification.type === 'spontaneousApplication'">
            {{ getApplicationFullName(application) }} vient de postuler spontanément à votre organisation.
          </p>
          <p v-else-if="notification.type === 'recruitment'">{{ getApplicationFullName(application) }} vient de rejoindre vos équipes.</p>
          <p v-else-if="notification.type === 'opinionSubmitted'">
            {{ notification.opinionAuthor ? getFullName(notification.opinionAuthor) : 'Un collaborateur' }} a donné son avis sur
            {{ getApplicationFullName(application) }}.
          </p>
          <p v-else-if="notification.type === 'opinionRequested'">
            Une candidature nécessite votre avis :
            <strong class="underline">{{ job || application.job ? jobTitle : 'Candidature spontanée' }}</strong>
          </p>
          <p v-else-if="notification.type === 'applicationStatusChange'">
            La candidature de {{ getApplicationFullName(application) }} a changé de statut
            <template v-if="job"
              >dans l’offre <strong>{{ jobTitle }}</strong></template
            >
            <template v-else>dans les candidatures spontanées</template>
          </p>
        </div>
      </div>
      <div v-else-if="notification.type === 'endDateClose'">
        <p>
          L'offre <strong>{{ jobTitle }}</strong> se clôture le {{ job.expiresAt }}.
        </p>
      </div>
      <div v-else-if="notification.type === 'endDateOver'">
        <p>
          Votre offre <strong>{{ jobTitle }}</strong> est maintenant clôturée.
        </p>
      </div>
      <div v-else-if="notification.type === 'endDateOverUrgent'">
        <p>
          Plusieurs candidats attendent votre retour pour l’offre <strong>{{ jobTitle }}</strong
          >.
        </p>
      </div>
      <div v-else-if="notification.type === 'jobCreated'">
        <p>
          L'offre <strong>{{ jobTitle }}</strong> a été créée.
        </p>
      </div>
      <div v-else-if="notification.type === 'jobScheduledPublished'">
        <p>
          L'offre <strong>{{ jobTitle }}</strong> a été publiée comme prévu.
        </p>
      </div>
      <div v-else-if="notification.type === 'jobScheduledFailed'">
        <p>
          La publication programmée de l'offre <strong>{{ jobTitle }}</strong> n'a pas pu être effectuée. Vérifiez votre abonnement et la date limite
          de candidature, puis publiez-la à nouveau.
        </p>
      </div>
      <div v-else-if="notification.type === 'recruiterAdded'">
        <p>
          Vous avez été ajouté à l’offre <strong>{{ jobTitle }}</strong
          >.
        </p>
      </div>
      <div v-else-if="notification.type === 'applicationStatusChange'">
        <p v-if="notification.job">
          {{ notification.application ? 'Une candidature a changé de statut' : 'Des candidatures ont changé de statut' }} sur l'offre
          <strong>{{ jobTitle }}</strong
          >.
        </p>
        <p v-else>
          {{ notification.application ? 'Une candidature spontanée a changé de statut' : 'Des candidatures spontanées ont changé de statut' }}.
        </p>
      </div>
      <div v-else-if="notification.type === 'recruitmentRequestToReview'">
        <p>
          La demande de recrutement <strong>{{ recruitmentRequest?.title }}</strong> attend votre validation.
        </p>
      </div>
      <div v-else-if="notification.type === 'recruitmentRequestApproved'">
        <p>
          Votre demande de recrutement <strong>{{ recruitmentRequest?.title }}</strong> a été validée.
        </p>
      </div>
      <div v-else-if="notification.type === 'recruitmentRequestRejected'">
        <p>
          Votre demande de recrutement <strong>{{ recruitmentRequest?.title }}</strong> a été refusée.
        </p>
      </div>
      <div v-else-if="notification.type === 'recruitmentRequestToPublish'">
        <p>
          La demande de recrutement <strong>{{ recruitmentRequest?.title }}</strong> attend votre publication.
        </p>
      </div>
    </div>
  </NuxtLink>
</template>
